"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateSatisfactionScoreByQuestionChoice = exports.calculateScoreSegmentationPerSatisfaction = exports.getOrderedNumericValueThresholds = exports.SENTIMENT_BOUNDS_THRESHOLD = void 0;
exports.SENTIMENT_BOUNDS_THRESHOLD = 0.36;
/**
 * This function returns the thresholds for the numerical values of the choices of a question,
 * where the numeric values are sorted and the top and bottom X choices are considered positive and negative.
 * The thresholds are used to determine whether the question is answered negatively or positively.
 * The indices of the negative and positive choices are determined by the score threshold constant.
 *
 * For example: if the score threshold is 0.36, the top 2 choices are positive, the 3rd middle choice is neutral,
 * and the bottom 2 choices are negative.
 */
const getOrderedNumericValueThresholds = (choicesNumericValues) => {
    var _a, _b, _c, _d;
    const filteredNumericValues = choicesNumericValues.filter((numericValue) => numericValue !== null);
    const sortedNumericValues = filteredNumericValues.sort((firstNumericValue, secondNumericValue) => firstNumericValue - secondNumericValue);
    // In case there are 3 choices, the formula below doesn't work for less than 4 choices.
    // the middle choice is neutral and the top and bottom choices are negative and positive respectively.
    if (sortedNumericValues.length === 3) {
        return {
            negativeNumericValueThreshold: (_a = sortedNumericValues[0]) !== null && _a !== void 0 ? _a : 0,
            positiveNumericValueThreshold: (_b = sortedNumericValues[2]) !== null && _b !== void 0 ? _b : 0,
        };
    }
    const negativeIndexThreshold = Math.ceil(sortedNumericValues.length * exports.SENTIMENT_BOUNDS_THRESHOLD) - 1;
    const positiveIndexThreshold = sortedNumericValues.length - Math.ceil(sortedNumericValues.length * exports.SENTIMENT_BOUNDS_THRESHOLD);
    return {
        negativeNumericValueThreshold: (_c = sortedNumericValues === null || sortedNumericValues === void 0 ? void 0 : sortedNumericValues[negativeIndexThreshold]) !== null && _c !== void 0 ? _c : 0,
        positiveNumericValueThreshold: (_d = sortedNumericValues === null || sortedNumericValues === void 0 ? void 0 : sortedNumericValues[positiveIndexThreshold]) !== null && _d !== void 0 ? _d : 0,
    };
};
exports.getOrderedNumericValueThresholds = getOrderedNumericValueThresholds;
const calculateScoreSegmentationPerSatisfaction = (questionChoicesToCalculate, thresholds) => {
    const initialState = {
        positive: 0,
        negative: 0,
        neutral: 0,
    };
    const sumNumericValues = questionChoicesToCalculate.reduce((accumulator, questionChoiceScore) => accumulator + questionChoiceScore.score, 0);
    // eslint-disable-next-line complexity
    const scorePerSatisfactionGroup = questionChoicesToCalculate.reduce((accumulator, questionChoiceScore) => {
        if (!(questionChoiceScore === null || questionChoiceScore === void 0 ? void 0 : questionChoiceScore.score) || questionChoiceScore.numeric_value === null) {
            return accumulator;
        }
        if (questionChoiceScore.numeric_value < thresholds.positive &&
            questionChoiceScore.numeric_value > thresholds.negative) {
            accumulator.neutral += questionChoiceScore.score;
        }
        else if (questionChoiceScore.numeric_value >= thresholds.positive) {
            accumulator.positive += questionChoiceScore.score;
        }
        else if (questionChoiceScore.numeric_value <= thresholds.negative) {
            accumulator.negative += questionChoiceScore.score;
        }
        return accumulator;
    }, initialState);
    const scorePerSatisfaction = Object.keys(scorePerSatisfactionGroup).reduce((accumulator, satisfactionGroupName) => {
        if (!sumNumericValues) {
            accumulator[satisfactionGroupName] = 0;
        }
        else {
            accumulator[satisfactionGroupName] =
                (scorePerSatisfactionGroup[satisfactionGroupName] / sumNumericValues) * 100;
        }
        return accumulator;
    }, scorePerSatisfactionGroup);
    return sumNumericValues ? scorePerSatisfaction : initialState;
};
exports.calculateScoreSegmentationPerSatisfaction = calculateScoreSegmentationPerSatisfaction;
/**
 * This function calculates the satisfaction score of a question based on its choices.
 * The score is calculated by the numeric value of the choices.
 * The numeric values are sorted and the top and bottom X choices are considered positive and negative.
 * The thresholds are used to determine whether the question is answered negatively or positively.
 */
const calculateSatisfactionScoreByQuestionChoice = (questionChoices) => {
    const sortedQuestionChoices = questionChoices.sort((choice1, choice2) => { var _a, _b; return Number((_a = choice1.numeric_value) !== null && _a !== void 0 ? _a : -1) - Number((_b = choice2.numeric_value) !== null && _b !== void 0 ? _b : -1); });
    const { negativeNumericValueThreshold, positiveNumericValueThreshold } = (0, exports.getOrderedNumericValueThresholds)(sortedQuestionChoices.map((choice) => { var _a; return (_a = choice.numeric_value) !== null && _a !== void 0 ? _a : -1; }));
    if (positiveNumericValueThreshold < negativeNumericValueThreshold) {
        return undefined;
    }
    const { positive: positiveScore, negative: negativeScore, neutral: neutralScore, } = (0, exports.calculateScoreSegmentationPerSatisfaction)(sortedQuestionChoices, {
        positive: positiveNumericValueThreshold,
        negative: negativeNumericValueThreshold,
    });
    const satisfactionScore = {
        positive: positiveScore,
        neutral: neutralScore,
        negative: negativeScore,
    };
    return satisfactionScore;
};
exports.calculateSatisfactionScoreByQuestionChoice = calculateSatisfactionScoreByQuestionChoice;
//# sourceMappingURL=QuestionChoiceUtils.js.map